import { memo, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router'
import { NodeProps, useReactFlow } from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import { NodeTypeEnum, PanelEnum } from '@ui/interfaces'
import useAppNavigation from '@ui/lib/hooks/useAppNavigation'
import { useNavigateToNode } from '@ui/lib/hooks/useNavigateToNode'
import { FeatureFlagName, useFeatureFlag } from '@ui/providers/FeatureFlagProvider'
import { RouteNamesEnum } from '@ui/router/routes'
import { useNodeAndEdgeStore } from '@ui/stores'
import { useLayoutPanelManagerStore } from '@ui/stores/pipelinesWorkflow'

import { LayoutPanel, RIGHT_PANEL_CONTENT } from '../PipelineLayoutManager'
import { NodeContainer } from './NodeContainer'
import { NodeContainerV2 } from './NodeContainerV2'

export const FirehoseNode = memo(() => {
  const workflowUpdateEnabled = useFeatureFlag(FeatureFlagName.WorkflowUpdate)

  const { fitView } = useReactFlow()
  const { navigateTo } = useAppNavigation()
  const location = useLocation()

  const navigateToNode = useNavigateToNode()

  const { currentNodeId, setCurrentNodeId, nodes, setDisplayModal } = useNodeAndEdgeStore(
    useShallow((state) => ({
      currentNodeId: state.currentNodeId,
      setCurrentNodeId: state.setCurrentNodeId,
      nodes: state.nodes,
      setDisplayModal: state.setDisplayModal,
    }))
  )

  const { setPanelContent } = useLayoutPanelManagerStore(
    useShallow((state) => ({
      setPanelContent: state.setPanelContent,
    }))
  )

  const firehoseNode = useMemo(
    () => nodes.find((node) => node.type === NodeTypeEnum.FIREHOSE) as unknown as NodeProps,
    [nodes]
  )

  const onClick = () => {
    setCurrentNodeId(firehoseNode.id)
    if (location.pathname.match(/pipelines/)) {
      if (workflowUpdateEnabled) {
        setPanelContent({
          panel: LayoutPanel.RIGHT_PANEL,
          content: RIGHT_PANEL_CONTENT.SOURCES,
          open: true,
        })

        navigateToNode(firehoseNode.id)
      } else {
        setDisplayModal(PanelEnum.SOURCES)
        navigateTo(RouteNamesEnum.EXISTING_PIPELINE)
      }
    }
  }

  useEffect(() => {
    if (nodes.find((node) => currentNodeId === node.id && node.type === NodeTypeEnum.FIREHOSE)) {
      fitView({ duration: 400, maxZoom: 1.25, nodes: nodes })
    } else if (currentNodeId) {
      fitView({ nodes: [{ id: currentNodeId }], duration: 400, maxZoom: 1.5, padding: 0.5 })
    }
  }, [fitView, nodes, currentNodeId])

  if (workflowUpdateEnabled) {
    return (
      <NodeContainerV2
        nodeIsSelected={currentNodeId === firehoseNode.id}
        nodeType={NodeTypeEnum.FIREHOSE}
        node={firehoseNode}
        onClick={onClick}
      />
    )
  } else {
    return (
      <NodeContainer
        nodeIsSelected={currentNodeId === firehoseNode.id}
        nodeType={NodeTypeEnum.FIREHOSE}
        node={firehoseNode as unknown as NodeProps}
        onClick={onClick}
      />
    )
  }
})
